import { recalledCommandForText } from '../../../packages/plugin-shared/commandInvocation.mjs';
import type { SetStateAction } from 'react';
import { applyMentionEdit, assertComposerMentions, insertMentionToken, type ComposerMention } from 'elowen-plugin-shared/composerMentions';
import type { BrainRecalledInput, BrainReplyReference } from '../../../src/shared/wireContract';

export type { ComposerMention };

type DraftAttachment = NonNullable<BrainRecalledInput['composer']>['attachments'][number];
const NO_ATTACHMENTS: DraftAttachment[] = [];
/** The complete composer intent captured before submission. File references point at existing uploads. */
export interface ComposerIntent {
  text: string;
  mentions: readonly ComposerMention[];
  attachments: readonly DraftAttachment[];
  recalled: readonly BrainRecalledInput[];
  replyTo: BrainReplyReference | null;
}
interface ParkedDraft { text: string; mentions: ComposerMention[]; recalled: BrainRecalledInput[]; attachments: DraftAttachment[]; replyTo: BrainReplyReference | null; visibleHold?: ComposerIntent }
const STASH_LIMIT = 10;

/** Provider-owned per-session drafts, shared by every composer and command. Only editors subscribe to keystrokes;
 *  the network controller reads the current snapshot on submit without broadcasting it to the chat. */
export function createComposerDraft() {
  let input = '';
  let recalled: BrainRecalledInput[] = [];
  let attachments: DraftAttachment[] = NO_ATTACHMENTS;
  let replyTo: BrainReplyReference | null = null;
  let visibleHold: ComposerIntent | undefined;
  const available = new Map<string, readonly BrainRecalledInput[]>();
  const inFlight = new Map<string, Set<string>>();
  const validReceipts = (id: string | null, rows: readonly BrainRecalledInput[]) =>
    rows.filter(row => id !== null && available.get(id)?.some(candidate => candidate.id === row.id));
  let mentions: ComposerMention[] = [];
  let session: string | null = null;
  const inputs = new Map<string, ParkedDraft>();
  const stashes = new Map<string | null, ParkedDraft[]>();
  const listeners = new Set<() => void>();
  const applyEdit = (start: number, end: number, inserted: string) => {
    // The range bookkeeping is the shared one: an edit moves every selection it does not touch, and drops
    // the one it covers rather than guessing where it went.
    const next = applyMentionEdit(input, mentions, start, end, inserted);
    visibleHold = undefined;
    input = next.text;
    mentions = [...next.mentions];
  };
  return {
    recall: (id: string, rows: readonly BrainRecalledInput[]): void => {
      available.set(id, rows);
      if (session === id) for (const listener of listeners) listener();
    },
    getRecalledInputs: (): readonly BrainRecalledInput[] => {
      if (!session) return [];
      const owned = new Set([...recalled.map(row => row.id), ...(stashes.get(session) ?? []).flatMap(draft => draft.recalled.map(row => row.id)), ...(inFlight.get(session) ?? [])]);
      return (available.get(session) ?? []).filter(row => !owned.has(row.id));
    },
    restoreRecalledInput: (id: string): void => {
      if (!session) throw new Error('No conversation is bound.');
      const owned = new Set([...recalled.map(row => row.id), ...(stashes.get(session) ?? []).flatMap(draft => draft.recalled.map(row => row.id)), ...(inFlight.get(session) ?? [])]);
      const row = available.get(session)?.find(candidate => candidate.id === id && !owned.has(id));
      if (!row) throw new Error('The recalled input is no longer available.');
      const text = row.composer?.text ?? row.text;
      const selected = row.composer?.mentions ?? [];
      assertComposerMentions(text, selected);
      const stack = stashes.get(session) ?? [];
      if (input || attachments.length || recalled.length || replyTo) {
        if (stack.length >= STASH_LIMIT) throw new Error('The conversation draft stash is full.');
        stack.push({ text: input, mentions, recalled, attachments, replyTo });
        stashes.set(session, stack);
      }
      visibleHold = undefined;
      input = text;
      mentions = [...selected];
      attachments = [...(row.composer?.attachments ?? [])];
      recalled = [row];
      replyTo = row.replyTo ?? null;
      for (const listener of listeners) listener();
    },
    commandForDraft: (text: string) => recalledCommandForText(recalled, text),
    getReplyTarget: () => replyTo,
    setReplyTarget: (value: BrainReplyReference | null) => { visibleHold = undefined; replyTo = value; for (const listener of listeners) listener(); },
    clearReplies: () => {
      visibleHold = undefined;
      replyTo = null;
      for (const draft of inputs.values()) { draft.replyTo = null; draft.visibleHold = undefined; }
      for (const stack of stashes.values()) for (const draft of stack) draft.replyTo = null;
      for (const listener of listeners) listener();
    },
    takeRecalled: (): BrainRecalledInput[] => {
      const rows = validReceipts(session, recalled);
      if (session && rows.length) {
        const held = inFlight.get(session) ?? new Set<string>();
        for (const row of rows) held.add(row.id);
        inFlight.set(session, held);
      }
      recalled = [];
      return rows;
    },
    settleRecalled: (id: string, rows: readonly BrainRecalledInput[], accepted: boolean): void => {
      const held = inFlight.get(id);
      for (const row of rows) held?.delete(row.id);
      if (held?.size === 0) inFlight.delete(id);
      if (accepted) {
        const consumed = new Set(rows.map(row => row.id));
        available.set(id, (available.get(id) ?? []).filter(row => !consumed.has(row.id)));
      }
      if (id === session) for (const listener of listeners) listener();
    },
    bindSession: (next: string) => {
      if (session === next) return;
      if (session !== null) {
        if (input || recalled.length || attachments.length || replyTo) inputs.set(session, { text: input, mentions, recalled, attachments, replyTo, visibleHold });
        else inputs.delete(session);
        const parked = inputs.get(next);
        input = parked?.text ?? '';
        mentions = parked?.mentions ?? [];
        recalled = parked?.recalled ?? [];
        attachments = parked?.attachments ?? NO_ATTACHMENTS;
        replyTo = parked?.replyTo ?? null;
        visibleHold = parked?.visibleHold;
      }
      if (session === null) {
        const pending = stashes.get(null);
        if (pending) { stashes.set(next, pending); stashes.delete(null); }
      }
      session = next;
      for (const listener of listeners) listener();
    },
    /** Put a refused message back into the draft of the conversation it was sent to, unless the person has
     *  already typed something newer there. `kept` are the mentions the caller still stands behind: a send
     *  refused for a stale mention comes back without that account. */
    restoreSession: (id: string, text: string, kept: readonly ComposerMention[] = [], receipts: BrainRecalledInput[] = [], files: DraftAttachment[] = [], reply: BrainReplyReference | null = null) => {
      assertComposerMentions(text, kept);
      const bound = session === id;
      const current = bound ? { text: input, mentions, recalled, attachments, replyTo } : inputs.get(id);
      const restored: ParkedDraft = { text, mentions: [...kept], recalled: receipts, attachments: files, replyTo: reply };
      if (current && (current.text || current.attachments.length || current.recalled.length || current.replyTo)) {
        const stack = stashes.get(id) ?? [];
        stack.push(restored);
        stashes.set(id, stack);
      } else if (bound) {
        input = restored.text;
        mentions = restored.mentions;
        recalled = restored.recalled;
        attachments = restored.attachments;
        replyTo = restored.replyTo;
        visibleHold = undefined;
      } else inputs.set(id, restored);
      if (bound) for (const listener of listeners) listener();
    },
    /** Show a held intent only when no newer draft exists; identity tracks subsequent edits without guessing. */
    showHeld: (id: string, intent: ComposerIntent): void => {
      assertComposerMentions(intent.text, intent.mentions);
      const bound = session === id;
      const current = bound ? { text: input, mentions, recalled, attachments, replyTo } : inputs.get(id);
      if (current && (current.text || current.attachments.length || current.recalled.length || current.replyTo)) return;
      const held: ParkedDraft = { text: intent.text, mentions: [...intent.mentions], recalled: [...intent.recalled], attachments: [...intent.attachments], replyTo: intent.replyTo, visibleHold: intent };
      if (bound) {
        input = held.text;
        mentions = held.mentions;
        recalled = held.recalled;
        attachments = held.attachments;
        replyTo = held.replyTo;
        visibleHold = intent;
        for (const listener of listeners) listener();
      } else inputs.set(id, held);
    },
    /** Preserve a complete uncertain send for explicit recall. Unlike manual stash, automatic recovery never
     *  evicts another draft: a full stack rejects before changing anything, so the caller keeps its hold.
     *  Newer text/mentions and file references stay; submitted receipts travel with the parked intent. */
    park: (id: string, intent: ComposerIntent): void => {
      assertComposerMentions(intent.text, intent.mentions);
      const stack = stashes.get(id) ?? [];
      if (stack.length >= STASH_LIMIT) throw new Error('The conversation draft stash is full.');
      stack.push({ text: intent.text, mentions: [...intent.mentions], recalled: [...intent.recalled], attachments: [...intent.attachments], replyTo: intent.replyTo });
      stashes.set(id, stack);
      if (session === id && visibleHold === intent) {
        input = '';
        mentions = [];
        recalled = [];
        attachments = NO_ATTACHMENTS;
        replyTo = null;
        visibleHold = undefined;
      } else if (session !== id && inputs.get(id)?.visibleHold === intent) inputs.delete(id);
      if (session === id) for (const listener of listeners) listener();
    },
    forgetSession: (id: string) => {
      inputs.delete(id);
      stashes.delete(id);
      available.delete(id);
      inFlight.delete(id);
      if (session === id) {
        input = '';
        mentions = [];
        recalled = [];
        attachments = NO_ATTACHMENTS;
        replyTo = null;
        visibleHold = undefined;
        for (const listener of listeners) listener();
      }
    },
    /** Session-local LIFO: text, receipt ids and files stay together. */
    stash: () => {
      const stack = stashes.get(session) ?? [];
      if (input.trim() || attachments.length || recalled.length || replyTo) {
        if (stack.length >= STASH_LIMIT) throw new Error('The conversation draft stash is full.');
        visibleHold = undefined;
        stack.push({ text: input, mentions, recalled, attachments, replyTo });
        stashes.set(session, stack);
        input = '';
        mentions = [];
        recalled = [];
        attachments = NO_ATTACHMENTS;
        replyTo = null;
      } else {
        const restored = stack.at(-1);
        if (restored === undefined) return;
        stack.pop();
        input = restored.text;
        mentions = restored.mentions;
        recalled = restored.recalled;
        attachments = restored.attachments;
        replyTo = restored.replyTo;
        if (!stack.length) stashes.delete(session);
      }
      for (const listener of listeners) listener();
    },
    getAttachments: () => attachments,
    getServerAttachments: () => NO_ATTACHMENTS,
    setAttachments: (update: SetStateAction<DraftAttachment[]>) => {
      visibleHold = undefined;
      attachments = typeof update === 'function' ? update(attachments) : update;
      for (const listener of listeners) listener();
    },
    getSnapshot: () => input,
    getServerSnapshot: () => '',
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },
    /** The mentions recorded for the current draft; a send narrows them with {@link mentionIdsIn}. */
    getMentions: (): readonly ComposerMention[] => mentions,
    /** Apply a known editor replacement, preserving only untouched selections. */
    applyMentionEdit: (start: number, end: number, inserted: string): void => {
      applyEdit(start, end, inserted);
      for (const listener of listeners) listener();
    },
    /** Replace `start..end` (the typed `@query`) with the account's (or the agent's) token and a space, and
     *  record the mention. Returns the caret position after the inserted space. */
    insertMention: (start: number, end: number, account: { id: number | 'agent'; label: string }): number => {
      const inserted = insertMentionToken(input, mentions, start, end, account);
      visibleHold = undefined;
      input = inserted.text;
      mentions = [...inserted.mentions];
      for (const listener of listeners) listener();
      return inserted.caret;
    },
    setInput: (update: SetStateAction<string>) => {
      const next = typeof update === 'function' ? update(input) : update;
      if (next === input) return;
      visibleHold = undefined;
      input = next;
      // Unknown replacements cannot prove that any selected occurrence survived.
      mentions = [];
      for (const listener of listeners) listener();
    },
  };
}
